/**
 * Boards index — top-level page mounted at /boards.
 * Layout: in-page left rail (Pinned + Your Content + Search) | selected board canvas.
 * Mirrors Mixpanel's IA without disrupting the portal's top nav.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Flex, Box, Spinner, Center, Text, Alert, AlertIcon,
  Button, VStack, HStack, Textarea, Input, Select,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, FormControl, FormLabel, FormHelperText,
  useDisclosure, useToast,
} from '@chakra-ui/react';
import { FiBarChart2, FiGrid, FiTrendingUp, FiPieChart, FiPlus } from 'react-icons/fi';
import Header from '../../components/Header';
import LeftRail from './components/LeftRail';
import { EmojiPickerField } from './components/EmojiPicker';
import BoardDetail from './BoardDetail';
import { boardsApi } from './api';
import { PRIMARY_BG, PRIMARY_BG_ACTIVE, PRIMARY_BG_HOVER } from '../../theme/brand';
import API from '../../helpers/axiosInstance';
import { getApiHeaders, hasAnyPermission, hasPermission } from '../../utils/DefaultFunction';

const BRAND   = PRIMARY_BG;
const BRAND_H = PRIMARY_BG_HOVER;
const BRAND_A = PRIMARY_BG_ACTIVE;
const DESC_MAX = 500;

function CreateFirstBoardModal({ isOpen, onClose, onCreated }) {
  const toast = useToast();
  const [name,        setName]        = useState('');
  const [emoji,       setEmoji]       = useState('📊');
  const [scope,       setScope]       = useState('personal');
  const [description, setDescription] = useState('');
  const [busy,        setBusy]        = useState(false);

  const reset = () => { setName(''); setEmoji('📊'); setScope('personal'); setDescription(''); };

  const save = async () => {
    if (!name.trim()) return toast({ status: 'warning', title: 'Board name is required' });
    if (description.length > DESC_MAX) return toast({ status: 'warning', title: `Description too long (max ${DESC_MAX} chars)` });
    setBusy(true);
    try {
      const r = await boardsApi.createBoard({ name: name.trim(), emoji, scope, description: description.trim() });
      toast({ status: 'success', title: 'Board created!', duration: 2000 });
      reset();
      onCreated && onCreated(r?.data);
      onClose();
    } catch (e) {
      toast({ status: 'error', title: 'Create failed', description: e?.response?.data?.error?.message || e.message });
    } finally { setBusy(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">Create your first board</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Board name</FormLabel>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Product KPIs" autoFocus />
            </FormControl>
            <HStack>
              <FormControl maxW="80px">
                <FormLabel fontSize="sm">Emoji</FormLabel>
                <EmojiPickerField value={emoji} onChange={setEmoji} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Scope</FormLabel>
                <Select value={scope} onChange={(e) => setScope(e.target.value)}>
                  <option value="personal">Personal (only you)</option>
                  <option value="team">Team (everyone in tenant)</option>
                </Select>
              </FormControl>
            </HStack>
            <FormControl>
              <FormLabel fontSize="sm">Description</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What questions does this board answer? Who is the audience?"
                rows={3}
                resize="vertical"
                maxLength={DESC_MAX}
              />
              <FormHelperText fontSize="xs" textAlign="right">{description.length} / {DESC_MAX}</FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button bg={BRAND} color="white" _hover={{ bg: BRAND_H }} _active={{ bg: BRAND_A }} isLoading={busy} onClick={save}>
            Create board
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// Feature tiles shown in the empty state
const FEATURES = [
  { icon: FiTrendingUp,  label: 'Time series',    desc: 'Track metrics over time' },
  { icon: FiBarChart2,   label: 'Breakdowns',      desc: 'Slice by any property' },
  { icon: FiPieChart,    label: 'KPI tables',      desc: 'Compare key numbers'   },
  { icon: FiGrid,        label: 'Flexible layout', desc: '1–4 column grids'      },
];

function EmptyInsights({ onCreateClick, canCreate = true }) {
  return (
    <Center h="100%" minH="70vh" px={6}>
      <VStack spacing={0} maxW="520px" w="100%" textAlign="center">
        {/* icon cluster */}
        <Box position="relative" mb={6}>
          <Box
            w="96px" h="96px" borderRadius="full"
            bg="linear-gradient(135deg, var(--brand-tint) 0%, var(--brand-tint-strong) 100%)"
            display="flex" alignItems="center" justifyContent="center"
            boxShadow="0 0 0 12px rgb(var(--brand-tint-rgb) / 0.333)"
          >
            <FiBarChart2 size={40} color={BRAND} />
          </Box>
          {/* orbiting mini icons */}
          {[
            { icon: FiTrendingUp, top: '-4px',  right: '-4px'  },
            { icon: FiPieChart,   bottom: '0px', left: '-8px'  },
            { icon: FiGrid,       top: '50%',    right: '-18px', transform: 'translateY(-50%)' },
          ].map(({ icon: Icon, ...pos }, i) => (
            <Box
              key={i}
              position="absolute"
              {...pos}
              w="28px" h="28px"
              bg="white"
              border="1.5px solid #e2e8f0"
              borderRadius="8px"
              display="flex" alignItems="center" justifyContent="center"
              boxShadow="0 2px 6px rgba(0,0,0,0.08)"
            >
              <Icon size={13} color={BRAND} />
            </Box>
          ))}
        </Box>

        {/* headline */}
        <Text fontSize="22px" fontWeight={700} color="#0f172a" lineHeight={1.3} mb={2}
          fontFamily='"Inter", sans-serif'>
          No boards yet
        </Text>
        <Text fontSize="sm" color="#64748b" lineHeight={1.7} mb={8} maxW="380px">
          Boards let you build custom dashboards from your customer data —
          combine metrics, charts, and segments in a flexible grid layout.
        </Text>

        {/* CTA */}
        {canCreate && (
        <Button
          leftIcon={<FiPlus />}
          bg={BRAND}
          color="white"
          size="md"
          px={7}
          py={5}
          borderRadius="10px"
          fontWeight={600}
          fontSize="sm"
          _hover={{ bg: BRAND_H, transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgb(var(--brand-primary-rgb) / 0.35)' }}
          _active={{ bg: BRAND_A, transform: 'none' }}
          transition="all 0.2s"
          onClick={onCreateClick}
          boxShadow="0 4px 14px rgb(var(--brand-primary-rgb) / 0.28)"
          mb={10}
        >
          Create your first board
        </Button>
        )}

        {/* feature grid */}
        <Box
          w="100%"
          display="grid"
          gridTemplateColumns="repeat(2, 1fr)"
          gap={3}
        >
          {FEATURES.map(({ icon: Icon, label, desc }) => (
            <HStack
              key={label}
              align="flex-start"
              spacing={3}
              bg="white"
              border="1px solid #f1f5f9"
              borderRadius="10px"
              px={4}
              py={3}
              textAlign="left"
              boxShadow="0 1px 3px rgba(0,0,0,0.04)"
            >
              <Box
                flexShrink={0}
                w="30px" h="30px"
                bg="var(--brand-tint)"
                borderRadius="8px"
                display="flex" alignItems="center" justifyContent="center"
              >
                <Icon size={14} color={BRAND} />
              </Box>
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="xs" fontWeight={600} color="#0f172a">{label}</Text>
                <Text fontSize="xs" color="#94a3b8">{desc}</Text>
              </VStack>
            </HStack>
          ))}
        </Box>
      </VStack>
    </Center>
  );
}

function getCurrentUserId() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return String(u?.id || u?.username || '');
  } catch { return ''; }
}

export default function Boards() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(paramId || null);
  const userId = getCurrentUserId();
  const createModal = useDisclosure();

  const location = useLocation();
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  // This one component serves both the list (/analytics/insights) and a single
  // board's detail view (/analytics/insights/:id) — backend groups permissions
  // under the list route only, so hardcode it rather than using the current path,
  // which would include the board id on the detail view and match nothing.
  const permParams = "analytics/insights";

  const fetchPermissions = async () => {
      setPermissionsLoading(true);
      try {
          const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`;
          const response = await API.get(url, { headers: getApiHeaders() });
          setPermissions(response.data.data || []);
      } catch (error) {
          console.error("Error fetching permissions:", error);
          setPermissions([]);
      } finally {
          setPermissionsLoading(false);
      }
  };
  useEffect(() => {
      fetchPermissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const boardsPermission = hasAnyPermission(["board-list", "board-view"], permissions);
  const boardListPermission = hasPermission("board-list", permissions);
  const boardViewPermission = hasPermission("board-view", permissions);
  const boardCreatePermission = hasPermission("board-create", permissions);
  const boardEditPermission = hasPermission("board-edit", permissions);
  const boardDeletePermission = hasPermission("board-delete", permissions);
  const boardSharePermission = hasPermission("board-share", permissions);

  const reloadBoards = () => boardsApi.list().then((res) => {
    // Same defensive guard as cohorts: never trust the response is an array.
    const list = Array.isArray(res?.data) ? res.data : [];
    setBoards(list);
    return list;
  });

  useEffect(() => {
    // This single fetch feeds both the board-list rail and the detail panel's
    // default-board selection / cohort data, so it must wait for permissions to
    // resolve and must not fire at all for a user with neither board-list nor
    // board-view (boardsPermission already covers that combined check).
    if (permissionsLoading || !boardsPermission) return;
    setLoading(true);
    Promise.all([
      reloadBoards(),
      boardsApi.listCohorts().catch(() => ({ data: [] })),
    ])
      .then(([list, c]) => {
        // listCohorts already normalizes to an array, but guard anyway —
        // an unexpected response shape must never crash the page.
        setCohorts(Array.isArray(c?.data) ? c.data : []);
        // RP_005: only auto-pick a board to display when the user can actually
        // browse the list — a board-view-only user (no board-list) must land on
        // a specific board by its own URL (paramId), never get an arbitrary
        // "first board" (personal, or the first team board) auto-opened for them.
        if (!activeId && boardListPermission && list.length > 0) setActiveId(String(list[0]._id));
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.error?.message || err?.message || 'Failed to load boards');
        setLoading(false);
      });
  }, [permissionsLoading, boardsPermission, boardListPermission]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (paramId) setActiveId(paramId);
  }, [paramId]);

  const pickBoard = (b) => {
    const id = String(b._id || b.id);
    setActiveId(id);
    navigate(`/analytics/insights/${id}`, { replace: false });
  };

  const onBoardCreated = async (newBoard) => {
    await reloadBoards();
    if (newBoard?._id) {
      setActiveId(String(newBoard._id));
      navigate(`/analytics/insights/${newBoard._id}`);
    }
  };

  if (permissionsLoading) {
    return (
      <>
        <Header header="Insights" />
        <Center h="60vh"><Spinner size="xl" /></Center>
      </>
    );
  }
  if (!boardsPermission) {
    return (
      <>
        <Header header="Insights" />
        <Center h="400px">
          <Box textAlign="center">
            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
              No Access
            </Text>
            <Text fontSize="sm" color="gray.500">
              You don't have permission to view this sections.
            </Text>
          </Box>
        </Center>
      </>
    );
  }
  if (loading) {
    return (
      <>
        <Header header="Insights" />
        <Center h="60vh"><Spinner size="xl" /></Center>
      </>
    );
  }
  if (error) {
    return (
      <>
        <Header header="Insights" />
        <Alert status="error" m={4}><AlertIcon />{error}</Alert>
      </>
    );
  }
  if (!boards || boards.length === 0) {
    return (
      <>
        <Header header="Insights" />
        <EmptyInsights onCreateClick={createModal.onOpen} canCreate={boardCreatePermission} />
        <CreateFirstBoardModal
          isOpen={createModal.isOpen}
          onClose={createModal.onClose}
          onCreated={onBoardCreated}
        />
      </>
    );
  }

  // The portal's <Header> renders the top nav. Boards' in-page layout (left rail
  // + main canvas) sits below it. We avoid <BoxWrapper> here on purpose — Boards
  // needs full-bleed height for the rail/canvas split.
  return (
    <>
      <Header header="Insights" />
      <Flex h="calc(100vh - 80px)" overflow="hidden" bg="gray.50">
        {boardListPermission && (
          <LeftRail boards={boards} currentUserId={userId} activeId={activeId}
            onPick={pickBoard} onBoardCreated={onBoardCreated} createPermission={boardCreatePermission} />
        )}
        <Box flex={1} overflowY="auto">
          {activeId ? (
            boardViewPermission ? (
              <BoardDetail boardId={activeId} cohorts={cohorts}
                editPermission={boardEditPermission} deletePermission={boardDeletePermission} sharePermission={boardSharePermission} />
            ) : (
              <Center h="60vh">
                <Box textAlign="center">
                  <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>No Access</Text>
                  <Text fontSize="sm" color="gray.500">You don't have permission to view this sections.</Text>
                </Box>
              </Center>
            )
          ) : boardListPermission ? (
            <Center h="60vh"><Text color="gray.500">Pick a board from the left.</Text></Center>
          ) : (
            <Center h="60vh">
              <Box textAlign="center">
                <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>No Access</Text>
                <Text fontSize="sm" color="gray.500">You don't have permission to browse boards — open one using its direct link.</Text>
              </Box>
            </Center>
          )}
        </Box>
      </Flex>
    </>
  );
}
